import { useState, useCallback, useEffect, useRef } from "react";
import {
  useGetAllInvoicesQuery,
  useDeleteInvoiceMutation,
} from "../api/invoiceApi";
import { InvoiceResponseType } from "../types/invoiceResponse";

// Just alias InvoiceResponseType as FeeItem for backward compatibility
export type FeeItem = InvoiceResponseType;

export const useInvoiceManagement = () => {
  const LAZY_PAGE_SIZE = 100;
  const [isAllMode, setIsAllMode] = useState(false);
  const [lazyPage, setLazyPage] = useState(1);
  const [accumulatedFees, setAccumulatedFees] = useState<FeeItem[]>([]);
  const [lazyHasMore, setLazyHasMore] = useState(true);

  const lastProcessedPage = useRef(0);
  const pageRequestedRef = useRef(false);

  const [selectedCenter, setSelectedCenter] = useState("all");
  const [filters, setFilters] = useState({
    size: 10,
    page: 1,
    search: "",
    center_id: undefined as number | undefined,
    course_id: undefined as number | undefined,
    status: "pending" as string | undefined,
    fee_type: undefined as string | undefined,
    from_due_date: undefined as string | undefined,
    to_due_date: undefined as string | undefined,
  });

  const {
    data: invoicesData,
    isLoading: loading,
    isFetching,
    error: apiError,
    refetch,
  } = useGetAllInvoicesQuery({
    ...filters,
    page: isAllMode ? lazyPage : filters.page,
    size: isAllMode ? LAZY_PAGE_SIZE : filters.size
  });

  const error = apiError ? "Failed to fetch invoices" : null;

  // Get fees from API response - updated to use items instead of data
  const fees = invoicesData?.data?.items || [];

  // Transform student data
  const transformedFees = fees.map(
    (fee: any): FeeItem => ({
      ...fee,
      student: fee.student
        ? {
            id: fee.student.id,
            name: fee.student.name || "",
          }
        : undefined,
    })
  );

  // Fetch fees with current filters
  const fetchFees = useCallback(() => {
    refetch();
  }, [refetch]);

  const resetLazyState = useCallback((initialFees: FeeItem[] = []) => {
    setAccumulatedFees(initialFees);
    setLazyPage(1);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
    setLazyHasMore(true);
  }, []);

  useEffect(() => {
    if (transformedFees && transformedFees.length >= 0) {
      if (isAllMode) {
        if (isFetching) return;
        if (lazyPage === lastProcessedPage.current) return;

        // No data -> end of results
        if (transformedFees.length === 0) {
          setLazyHasMore(false);
          pageRequestedRef.current = false;
          return;
        }

        setLazyHasMore(transformedFees.length === LAZY_PAGE_SIZE);
        lastProcessedPage.current = lazyPage;
        pageRequestedRef.current = false;

        setAccumulatedFees((prev) => {
          if (lazyPage === 1) return transformedFees;
          const newUnique = transformedFees.filter(
            (newS: any) => !prev.some((p: any) => p.id === newS.id)
          );
          return [...prev, ...newUnique];
        });
        
        const total = invoicesData?.data?.totalItems || 0;
        const fetchedSoFar = lazyPage * LAZY_PAGE_SIZE;
        if (transformedFees.length < LAZY_PAGE_SIZE || fetchedSoFar >= total) {
          setLazyHasMore(false);
        } else {
          // Auto-fetch next chunk
          if (!pageRequestedRef.current) {
            pageRequestedRef.current = true;
            setLazyPage(prev => prev + 1);
          }
        }
      }
    }
  }, [transformedFees, isAllMode, lazyPage, isFetching, invoicesData?.data?.totalItems]);

  // Update filters - MODIFIED to not reset page when changing pages
  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters((prev) => {
      // Don't reset page to 1 if we're explicitly changing the page
      if (newFilters.page !== undefined) {
        return { ...prev, ...newFilters };
      }
      // Reset page to 1 for other filter changes
      return { ...prev, ...newFilters, page: 1 };
    });
  }, []);

  // Get filtered fees based on center
  const getFilteredFees = useCallback(() => {
    const dataSource = isAllMode ? accumulatedFees : transformedFees;
    if (selectedCenter === "all") return dataSource;
    return dataSource.filter(
      (fee: FeeItem) => fee.center.id.toString() === selectedCenter
    );
  }, [transformedFees, accumulatedFees, isAllMode, selectedCenter]);

  // Get center options for dropdown
  const getCenterOptions = useCallback(() => {
    const centerOptions = [
      { value: "all", label: "All Centers" },
      ...Array.from(
        new Set(transformedFees.map((fee: FeeItem) => fee.center.id))
      ).map((centerId) => {
        const center = transformedFees.find(
          (fee: FeeItem) => fee.center.id === centerId
        )?.center;
        return {
          value: centerId.toString(),
          label: center?.name || "Center ${centerId}",
        };
      }),
    ];
    return centerOptions;
  }, [transformedFees]);

  // Get course options for dropdown
  const getCourseOptions = useCallback(() => {
    return Array.from(
      new Set(
        transformedFees
          .filter((f: FeeItem) => f.course)
          .map((fee: FeeItem) => fee.course?.id)
      )
    ).map((courseId) => {
      const course = transformedFees.find(
        (fee: FeeItem) => fee.course?.id === courseId
      )?.course;
      return {
        value: courseId?.toString() || "",
        label: course?.name || "Course ${courseId}",
      };
    });
  }, [transformedFees]);

  // Pagination helpers
  const paginationData = {
    totalPages: invoicesData?.data?.totalPages || 0,
    pageSize: invoicesData?.data?.pageSize || 10,
    totalItems: invoicesData?.data?.totalItems || 0,
    currentPage: filters.page || 1, // Use the current filter page as source of truth
  };

  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();

  const handleDeleteInvoice = useCallback(
    async (id: number | string) => {
      try {
        const response = await deleteInvoice({ id }).unwrap();
        if (response.success) {
          refetch();
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error deleting invoice:", error);
        throw error;
      }
    },
    [deleteInvoice, refetch]
  );

  return {
    fees: transformedFees,
    loading,
    error,
    selectedCenter,
    setSelectedCenter,
    fetchFees,
    // collectFee,
    getFilteredFees,
    getCenterOptions,
    getCourseOptions,
    updateFilters,
    paginationData,
    filters,
    handleDeleteInvoice,
    isDeleting,
    isAllMode,
    setIsAllMode,
    accumulatedFees,
    resetLazyState,
  };
};
